<template>
  <div class="h-full flex gap-6">
    <!-- Left Side: Fixed Content -->
    <div class="w-[400px] flex-shrink-0 pl-6 py-6">
      <div class="space-y-4 h-full flex flex-col">
        <!-- Info Message -->
        <div class=" rounded-lg shadow flex-shrink-0 p-1">
          <div class="flex items-center justify-center h-[36px]">
            <p class="text-sm text-gray-600">
              최근 10영업일의 거래내역을 제공합니다.
            </p>
          </div>
        </div>

        <!-- Bond Type Selection -->
        <div class="bg-white rounded-lg shadow p-4 flex-shrink-0">
          <h3 class="text-sm font-medium text-gray-700 mb-4">유형</h3>
          <BondTypeConditionComponent @bondGroupsUpdated="handleBondGroupsUpdate" onlyCredit="true" />
        </div>

        <!-- Maturity Range -->
        <div class="bg-white rounded-lg shadow p-4 flex-shrink-0">
          <h3 class="text-sm font-medium text-gray-700 mb-4">만기</h3>
          <MaturityConditionComponent v-model="selectedMaturityRange" />
        </div>

        <!-- Chart Section -->
        <div class="bg-white rounded-lg shadow p-4 flex-shrink-0">
          <!-- 차트 타입 버튼 -->
          <div class="flex flex-wrap items-center gap-2 mb-4">
            <button class="px-3 py-1.5 text-sm rounded-md bg-gray-700 text-white">
              일자별 체결금리 추이
            </button>
          </div>
          <div class="h-[300px]">
            <TransactionYieldBandChart v-if="bondType.bondTypeId" :transactions="filteredTransactions" />
            <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">
              채권 유형을 선택해주세요
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Side: Scrollable Transaction List -->
    <div class="flex-1 pr-6 py-6">
      <div class="bg-white rounded-lg shadow h-full flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="p-4 border-b border-gray-200">
          <div class="flex justify-between items-center">
            <h2 class="text-sm font-medium text-gray-700">조회 결과</h2>
            <div class="flex items-center gap-4">
              <!-- 검색창 -->
              <div class="relative w-64">
                <input v-model="inputKeyword" type="text" placeholder="검색어를 입력하세요"
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  @keyup.enter="applySearch" />
                <!-- 초기화 버튼 -->
                <button v-if="inputKeyword" @click="clearSearch"
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button @click="refreshData" class="text-gray-600 hover:text-gray-900" :disabled="isLoading">
                <div class="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="{ 'animate-spin': isLoading }"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span class="text-sm">새로고침</span>
                </div>
              </button>
              <div class="text-sm text-gray-600">
                {{ formattedDate }}
              </div>
            </div>
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-1 overflow-auto">
          <div v-if="isLoading" class="text-center py-4">
            데이터를 불러오는 중...
          </div>
          <div v-else-if="errorMessage" class="text-center py-4 text-red-600">
            {{ errorMessage }}
          </div>
          <div v-else>
            <TransactionInquiryResultComponent :transactions="filteredTransactions" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { getTodayStr, formatDate } from '@/utils/util';
import transactionService from '@/service/transactionService';
import BondTypeConditionComponent from '@/components/conditions/BondTypeConditionComponent.vue';
import MaturityConditionComponent from '@/components/conditions/MaturityConditionComponent.vue';
import TransactionInquiryResultComponent from '@/components/transaction/TransactionInquiryResultComponent.vue';
import TransactionYieldBandChart from '@/components/TransactionYieldBandChart.vue';

export default {
  name: 'TransactionView',
  components: {
    BondTypeConditionComponent,
    MaturityConditionComponent,
    TransactionInquiryResultComponent,
    TransactionYieldBandChart,
  },

  setup() {
    const store = useStore();

    // 컴포넌트 마운트 시 채권 유형 초기화
    onMounted(() => {
      store.dispatch('filter/setBondType', {
        bondTypeId: null,
        filterMode: null,
        selectedGroupId: null,
        selectedRatings: [],
        issuerIds: null,
      });
    });

    // State
    const allTransactions = ref([]);
    const today = ref(getTodayStr());
    const formattedDate = computed(() => formatDate(today.value));
    const selectedMaturityRange = ref([0, 61]);
    const inputKeyword = ref('');
    const activeKeyword = ref('');
    const isLoading = ref(false);
    const errorMessage = ref(null);

    // Store에서 상태 가져오기
    const bondType = computed(() => store.state.filter.bondType);
    const bondGroups = computed(() => store.state.filter.bondGroups);

    // Fetch Data
    const fetchTransactions = async () => {
      if (!bondType.value.bondTypeId) return;

      isLoading.value = true;
      errorMessage.value = null;
      try {
        const transactions = await transactionService.inquiry(
          bondType.value.bondTypeId,
          0, // 최소 만기 - 모든 데이터 가져오기
          61, // 최대 만기 - 모든 데이터 가져오기  
          null // 키워드 없이 모든 데이터 가져오기
        );
        allTransactions.value = transactions;
      } catch (err) {
        console.error('Error fetching transactions:', err);
        errorMessage.value =
          '데이터 조회 중 오류가 발생했습니다. 다시 시도해주세요.';
      } finally {
        isLoading.value = false;
      }
    };

    // Refresh data without changing state
    const refreshData = async () => {
      if (isLoading.value) return;
      await fetchTransactions();
    };

    // bondGroups 업데이트 핸들러
    const handleBondGroupsUpdate = groups => {
      store.dispatch('filter/setBondGroups', groups);
    };

    // 검색어 적용
    const applySearch = () => {
      activeKeyword.value = inputKeyword.value;
    };

    // 검색어 초기화
    const clearSearch = () => {
      inputKeyword.value = '';
      activeKeyword.value = '';
    };

    // Computed
    const filteredTransactions = computed(() => {
      let filtered = allTransactions.value;

      // 필터 모드에 따른 필터링
      if (bondType.value.filterMode === 'group' && bondType.value.selectedGroupId) {
        const group = bondGroups.value.find(g => g.id === bondType.value.selectedGroupId);
        if (group && group.issuerIds) {
          filtered = filtered.filter(tx =>
            group.issuerIds.includes(tx.bondIssuerId)
          );
        }
      } else if (bondType.value.filterMode === 'rating') {
        if (!bondType.value.selectedRatings?.length) {
          filtered = [];
        } else {
          filtered = filtered.filter(tx =>
            bondType.value.selectedRatings.includes(tx.grade)
          );
        }
      }

      // 만기 필터 적용
      if (selectedMaturityRange.value) {
        const [minMonths, maxMonths] = selectedMaturityRange.value;
        filtered = filtered.filter(tx => {
          const maturityDate = new Date(tx.maturityDate);
          const today = new Date();
          const monthsDiff =
            (maturityDate.getFullYear() - today.getFullYear()) * 12 +
            (maturityDate.getMonth() - today.getMonth());
          return (
            monthsDiff >= minMonths &&
            (maxMonths === 61 || monthsDiff <= maxMonths)
          );
        });
      }

      // 키워드 검색 적용
      if (activeKeyword.value.trim()) {
        filtered = filtered.filter(tx =>
          tx.originalBondName
            .toLowerCase()
            .includes(activeKeyword.value.toLowerCase())
        );
      }

      return filtered;
    });

    // Watchers
    watch(
      () => bondType.value.bondTypeId,
      async newId => {
        if (newId) {
          await fetchTransactions();
        } else {
          allTransactions.value = [];
        }
      }
    );

    return {
      // State
      allTransactions,
      selectedMaturityRange,
      inputKeyword,
      activeKeyword,
      formattedDate,
      isLoading,
      errorMessage,

      // Computed
      bondType,
      bondGroups,
      filteredTransactions,

      // Methods
      refreshData,
      handleBondGroupsUpdate,
      applySearch,
      clearSearch,
    };
  },
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
